import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { alpha, styled } from "@mui/material/styles";
import { Button, CircularProgress, FormHelperText } from "@mui/material";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import createInstance from "../../../api/axiosinstance";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { useThemeMode } from "../../Themes/ThemeContextTemp.js";

const CssTextField = styled(TextField)({
  "& label": {},
  "& .MuiInput-underline:after": {},
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderRadius: 10,
    },
  },
});

const CssFormControl = styled(FormControl)({
  "& label": {},
  "& .MuiInputBase-root": {
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 10,
    },
  },
});

const CssSelect = styled(Select)({
  "& .MuiSvgIcon-root": {
    textAlign: "center",
  },
});

const AddOrganization = ({ orgData }) => {
  const aiDishaApi = createInstance("DISHA");
  const [creationDate] = useState(new Date().toISOString());
  const isEditMode = orgData ? true : false;
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [emailId, setEmailId] = useState("");
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [sector, setSector] = useState("");
  const [orgId, setOrgId] = useState(
    isEditMode ? orgData.organization_id : null
  );
  const [activeSectors, setActiveSectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orgLogo, setOrgLogo] = useState(null); 
  const { COLORS } = useThemeMode();

  const [errors, setErrors] = useState({});

  const clearForm = () => {
    if (isEditMode) {
      setName(orgData.organization_name);
      setDomain(orgData.organization_domain);
      setEmailId(orgData.email);
      setContact(orgData.contact_number);
      setAddress(orgData.organization_address);
      setSector(orgData.sector);
      setOrgLogo(null);
    } else {
      setName("");
      setDomain("");
      setEmailId("");
      setContact("");
      setAddress("");
      setSector("");
      setOrgLogo(null);
    }
    setErrors({});
  };

  useEffect(() => {
    getActiveSectors();

    if (orgData) {
      setName(orgData.organization_name);
      setDomain(orgData.organization_domain);
      setEmailId(orgData.email);
      setContact(orgData.contact_number);
      setAddress(orgData.organization_address);
      setSector(orgData.sector);
      setOrgId(orgData.organization_id);
    }
  }, [orgData]);

  const getActiveSectors = async () => {
    try {
      const response = await aiDishaApi.get("/sectors-show-active");
      setActiveSectors(response.data["sectors"]);
    } catch (error) {
      console.error("Error fetching sectors:", error);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setOrgLogo(file);
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Organization name is required.";
    
    const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!domain.trim()) newErrors.domain = "Organization domain is required.";
    else if (!domainPattern.test(domain.trim())) newErrors.domain = "Organization domain is invalid.";

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailId.trim()) newErrors.emailId = "Email ID is required.";
    else if (!emailPattern.test(emailId.trim())) newErrors.emailId = "Please enter a valid email address.";

    if (!contact.trim()) newErrors.contact = "Contact number is required.";
    else if (contact.trim().length !== 10) newErrors.contact = "Contact number must be 10 digits.";

    if (!address.trim()) newErrors.address = "Organization address is required.";
    if (!sector) newErrors.sector = "Sector is required.";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("organization_name", name.trim());
      formData.append("organization_domain", domain.trim());
      formData.append("email", emailId.trim());
      formData.append("contact_number", contact);
      formData.append("organization_address", address.trim());
      formData.append("status", "Active");
      
      if (!isEditMode) {
        formData.append("creation_date", creationDate);
      }
      if (orgLogo) {
        formData.append("org_logo", orgLogo); 
      }

      if (isEditMode) {
        const response = await aiDishaApi.put(
          `/edit-organization/${orgId}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success("Organization updated successfully");
      } else {
        const response = await aiDishaApi.post("/add-organization", formData, {
          params: { sector: sector },
          headers: { "Content-Type": "multipart/form-data" },
        });
        clearForm();
        toast.success("Organization added successfully");
      }
    } catch (error) {
      console.error("Error with organization operation:", error);
      
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const backendErrors = {};
        error.response.data.detail.forEach((err) => {
          const fieldName = err.loc[err.loc.length - 1]; 
          
          if (fieldName === "organization_name") backendErrors.name = err.msg;
          else if (fieldName === "organization_domain") backendErrors.domain = err.msg;
          else if (fieldName === "email") backendErrors.emailId = err.msg;
          else if (fieldName === "contact_number") backendErrors.contact = err.msg;
          else if (fieldName === "organization_address") backendErrors.address = err.msg;
          else if (fieldName === "sector") backendErrors.sector = err.msg;
          else backendErrors[fieldName] = err.msg;
        });

        if (Object.keys(backendErrors).length > 0) {
          setErrors(backendErrors);
          setLoading(false);
          return; 
        }
      }

      let errorMessage = isEditMode ? "Error updating organization" : "Error adding organization";
      if (error.response?.data) {
        if (typeof error.response.data.detail === "string") {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={{
          marginTop: "auto",
          alignItems: "center",
          borderRadius: "10px",
          padding: "1% 10%",
        }}
      >
        <Box gridColumn="span 12" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "0%" }}>
          <Typography sx={{ fontFamily: "poppins", fontSize: "28px", color: COLORS.text }}>
            {isEditMode ? "Edit Organization" : "Add Organization"}
          </Typography>
        </Box>

        {/* Organization Name Field */}
        <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}>
          <Typography sx={{ fontFamily: "poppins", color: COLORS.text, textAlign: "left" }}>
            Organization Name<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssTextField
            placeholder="Enter Organization Name"
            size="small"
            error={!!errors.name}
            helperText={errors.name}
            sx={{
              width: "95%",
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: errors.name ? "red" : COLORS.border },
                "&:hover fieldset": { borderColor: errors.name ? "red" : COLORS.border },
                "&.Mui-focused fieldset": { borderColor: errors.name ? "red" : COLORS.border },
                "& .MuiInputBase-input": { color: COLORS.text },
              },
            }}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors({ ...errors, name: null });
            }}
          />
        </Box>

        {/* Organization Domain Field */}
        <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}>
          <Typography sx={{ fontFamily: "poppins", color: COLORS.text, textAlign: "left" }}>
            Organization Domain<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssTextField
            placeholder="Enter Organization Domain"
            size="small"
            error={!!errors.domain}
            helperText={errors.domain}
            sx={{
              width: "95%",
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: errors.domain ? "red" : COLORS.border },
                "&:hover fieldset": { borderColor: errors.domain ? "red" : COLORS.border },
                "&.Mui-focused fieldset": { borderColor: errors.domain ? "red" : COLORS.border },
                "& .MuiInputBase-input": { color: COLORS.text },
              },
            }}
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value);
              if (errors.domain) setErrors({ ...errors, domain: null });
            }}
          />
        </Box>

        {/* Email ID Field */}
        <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}>
          <Typography sx={{ fontFamily: "poppins", color: COLORS.text, textAlign: "left" }}>
            Email ID<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssTextField
            placeholder="Enter contact mail id"
            size="small"
            error={!!errors.emailId}
            helperText={errors.emailId}
            sx={{
              width: "95%",
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: errors.emailId ? "red" : COLORS.border },
                "&:hover fieldset": { borderColor: errors.emailId ? "red" : COLORS.border },
                "&.Mui-focused fieldset": { borderColor: errors.emailId ? "red" : COLORS.border },
                "& .MuiInputBase-input": { color: COLORS.text },
              },
            }}
            value={emailId}
            onChange={(e) => {
              setEmailId(e.target.value);
              if (errors.emailId) setErrors({ ...errors, emailId: null });
            }}
          />
        </Box>

        {/* Contact Number Field */}
        <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}>
          <Typography sx={{ fontFamily: "poppins", color: COLORS.text, textAlign: "left" }}>
            Contact Number<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssTextField
            placeholder="Enter contact number"
            size="small"
            error={!!errors.contact}
            helperText={errors.contact}
            inputProps={{ maxLength: 10, inputMode: "numeric" }}
            sx={{
              width: "95%",
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: errors.contact ? "red" : COLORS.border },
                "&:hover fieldset": { borderColor: errors.contact ? "red" : COLORS.border },
                "&.Mui-focused fieldset": { borderColor: errors.contact ? "red" : COLORS.border },
                "& .MuiInputBase-input": { color: COLORS.text },
              },
            }}
            value={contact}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, "");
              setContact(digitsOnly);
              if (errors.contact) setErrors({ ...errors, contact: null });
            }}
          />
        </Box>

        {/* Organization Address Field */}
        <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}>
          <Typography sx={{ fontFamily: "poppins", color: COLORS.text, textAlign: "left" }}>
            Organization Address<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssTextField
            placeholder="Enter Organization Address"
            size="small"
            error={!!errors.address}
            helperText={errors.address}
            sx={{
              width: "98%",
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: errors.address ? "red" : COLORS.border },
                "&:hover fieldset": { borderColor: errors.address ? "red" : COLORS.border },
                "&.Mui-focused fieldset": { borderColor: errors.address ? "red" : COLORS.border },
                "& .MuiInputBase-input": { color: COLORS.text },
              },
            }}
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              if (errors.address) setErrors({ ...errors, address: null });
            }}
          />
        </Box>

        {/* Sector Select Field */}
        <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}>
          <Typography sx={{ fontFamily: "poppins", color: COLORS.text, textAlign: "left" }}>
            Sector<span style={{ color: "red" }}>*</span>
          </Typography>
          <CssFormControl 
            error={!!errors.sector}
            size="small"
            sx={{
              width: "95%",
              "& .MuiInputBase-root": {
                color: COLORS.text,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: errors.sector ? "red" : COLORS.border },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: errors.sector ? "red" : COLORS.border },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: errors.sector ? "red" : COLORS.border },
              },
              "& .MuiSvgIcon-root": { color: COLORS.text },
            }}
          >
            <CssSelect
              displayEmpty
              value={sector}
              onChange={(e) => {
                setSector(e.target.value);
                if (errors.sector) setErrors({ ...errors, sector: null });
              }}
              sx={{ width: "100%", textAlign: "left" }}
            >
              <MenuItem value="" disabled sx={{ color: COLORS.text }}>
                <em>Select Sector</em>
              </MenuItem>
              {activeSectors.map((sector, index) => (
                <MenuItem
                  key={sector.sector_id || `${sector.sector_name}-${index}`}
                  value={sector.sector_name}
                  sx={{ color: COLORS.text }}
                >
                  {sector.sector_name}
                </MenuItem>
              ))}
            </CssSelect>
            {errors.sector && <FormHelperText sx={{ color: "red" }}>{errors.sector}</FormHelperText>}
          </CssFormControl>
        </Box>

        {/* Organization Logo Field */}
        <Box gridColumn="span 6" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}>
          <Typography sx={{ fontFamily: "poppins", color: COLORS.text, textAlign: "left" }}>
            Organization Logo
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            style={{ marginTop: "8px", color: COLORS.text }}
          />
          {orgLogo && (
            <Typography sx={{ fontFamily: "poppins", color: COLORS.text, marginTop: "8px" }}>
              Selected: {orgLogo.name}
            </Typography>
          )}
        </Box>

        {/* Action Buttons */}
        <Box gridColumn="span 12" sx={{ marginTop: "auto", textAlign: "right", width: "90%" }}>
          <Button
            sx={{
              border: "1px solid",
              color: COLORS.button, 
              "&:hover": { color: COLORS.hover_btn },
              width: "120px",
              height: "35px",
              borderRadius: "10px",
              marginRight: "2%",
            }}
            onClick={clearForm}
            disabled={loading}
          >
            {isEditMode ? "Reset" : "Clear"}
          </Button>
          <Button
            sx={{
              border: "1px solid",
              background: COLORS.button, 
              color: COLORS.background, 
              "&:hover": { background: COLORS.hover_btn },
              width: "120px",
              height: "35px",
              borderRadius: "10px",
            }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: COLORS.background, position: "absolute", top: "50%", left: "50%", marginTop: "-12px", marginLeft: "-12px" }} />
            ) : isEditMode ? (
              "Update"
            ) : (
              "Submit"
            )}
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default AddOrganization;